<script>
  // Handles the links Firebase emails: confirm an address, reset a password, restore a
  // changed address. Shown over everything else while the URL carries mode and oobCode.
  import { createEventDispatcher, onMount } from "svelte";
  import { sendVerificationEmail, clearActionFromUrl } from "../verification.js";
  export let auth;
  export let action; // { mode, code }
  const dispatch = createEventDispatcher();

  let state = "working"; // working | done | error | resetForm
  let title = "One moment";
  let text = "";
  let email = "";
  let password = "";
  let busy = false;
  let resent = false;

  const friendly = (e) => {
    const code = e && e.code ? e.code : "";
    if (code === "auth/expired-action-code") return "This link has expired.";
    if (code === "auth/invalid-action-code") return "This link has already been used, or is not complete.";
    if (code === "auth/user-disabled") return "This account has been disabled.";
    if (code === "auth/user-not-found") return "This account no longer exists.";
    if (code === "auth/weak-password") return "Please choose a longer password (at least 8 characters).";
    return "Something went wrong. Please try again.";
  };

  async function alreadyVerified() {
    const u = auth.currentUser;
    if (!u) return false;
    try { await u.reload(); } catch (e) { /* ignore */ }
    return !!(auth.currentUser && auth.currentUser.emailVerified);
  }

  onMount(async () => {
    try {
      if (action.mode === "verifyEmail") {
        try {
          await auth.applyActionCode(action.code);
        } catch (e) {
          if (!(await alreadyVerified())) throw e;
        }
        if (auth.currentUser) { try { await auth.currentUser.reload(); } catch (e) { /* ignore */ } }
        title = "Email confirmed";
        text = auth.currentUser
          ? "Thank you. Your window can open now."
          : "Thank you. Sign in on the device you use as a window, and it can open now.";
        state = "done";
      } else if (action.mode === "recoverEmail") {
        await auth.applyActionCode(action.code);
        title = "Email address restored";
        text = "Your account is back on its previous address. If you did not ask for the change, reset your password too.";
        state = "done";
      } else if (action.mode === "resetPassword") {
        email = await auth.verifyPasswordResetCode(action.code);
        title = "Choose a new password";
        text = `For ${email}.`;
        state = "resetForm";
      } else {
        throw new Error("unknown mode");
      }
    } catch (e) {
      title = action.mode === "resetPassword" ? "This reset link cannot be used" : "This link cannot be used";
      text = friendly(e);
      state = "error";
    }
  });

  async function savePassword() {
    if (password.length < 8 || busy) return;
    busy = true;
    try {
      await auth.confirmPasswordReset(action.code, password);
      title = "Password changed";
      text = "Sign in with your new password.";
      state = "done";
    } catch (e) {
      text = friendly(e);
      state = "error";
    } finally {
      busy = false;
    }
  }

  async function resend() {
    if (!auth.currentUser || busy) return;
    busy = true;
    try {
      await sendVerificationEmail(auth.currentUser);
      resent = true;
      text = "A new link is on its way to " + auth.currentUser.email + ".";
    } catch (e) {
      text = e && e.code === "auth/too-many-requests" ? "Too many attempts. Please wait a few minutes." : "Could not send a new link. Please try again.";
    } finally {
      busy = false;
    }
  }

  function finish() {
    clearActionFromUrl();
    dispatch("done");
  }
</script>

<div class="action" role="dialog" aria-labelledby="action-title">
  <div class="action__card">
    <p class="action__brand">PassCall</p>
    <h1 id="action-title" class="action__title">{title}</h1>
    {#if state === "working"}
      <p class="action__text">Checking your link…</p>
    {:else}
      <p class="action__text">{text}</p>
    {/if}

    {#if state === "resetForm"}
      <form class="action__form" on:submit|preventDefault={savePassword}>
        <label class="action__label" for="newPassword">New password</label>
        <input id="newPassword" class="action__input" type="password" bind:value={password} minlength="8" autocomplete="new-password" placeholder="At least 8 characters" />
        <button type="submit" class="action__btn" disabled={busy || password.length < 8}>{busy ? "Saving…" : "Save password"}</button>
      </form>
    {:else if state === "done"}
      <button type="button" class="action__btn" on:click={finish}>Continue</button>
    {:else if state === "error"}
      <div class="action__actions">
        {#if action.mode === "verifyEmail" && auth.currentUser && !resent}
          <button type="button" class="action__btn" on:click={resend} disabled={busy}>{busy ? "Sending…" : "Send a new link"}</button>
        {/if}
        <button type="button" class="action__btn action__btn--ghost" on:click={finish}>Continue</button>
      </div>
    {/if}
  </div>
</div>

<style>
  .action {
    position: fixed;
    inset: 0;
    z-index: 100005;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: var(--green, #0f2a1e);
    color: var(--ink, #15100e);
    font: 400 1rem/1.5 Inter, system-ui, sans-serif;
  }
  .action__card {
    width: min(100%, 30rem);
    padding: 1.6rem 1.5rem;
    border-radius: 18px;
    background: var(--paper, #fffcf7);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
  }
  .action__brand {
    margin: 0 0 0.6rem;
    font: 600 0.8rem/1 Inter, system-ui, sans-serif;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--gold, #d4af37);
  }
  .action__title { margin: 0 0 0.5rem; font: 600 1.6rem/1.2 Georgia, "Times New Roman", serif; }
  .action__text { margin: 0 0 1.2rem; color: #4d453f; }
  .action__form { display: flex; flex-direction: column; gap: 0.6rem; }
  .action__label { font-weight: 600; font-size: 0.9rem; }
  .action__input {
    padding: 0.7rem 0.8rem;
    border: 1px solid #c9c1b6;
    border-radius: 10px;
    font: inherit;
    background: var(--cream, #f4ede3);
  }
  .action__actions { display: flex; flex-wrap: wrap; gap: 0.6rem; }
  .action__btn {
    border: 1px solid var(--ink, #15100e);
    border-radius: 999px;
    padding: 0.6rem 1.2rem;
    background: var(--gold, #d4af37);
    color: var(--ink, #15100e);
    font: 600 0.95rem/1 Inter, system-ui, sans-serif;
    cursor: pointer;
  }
  .action__btn:disabled { opacity: 0.6; cursor: default; }
  .action__btn--ghost { background: transparent; }
</style>
